import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/ProductsService';
import {Product} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useProduct = (productId?: string) => {
    const queryClient = useQueryClient();

    const productQuery = useQuery({
        queryKey: ['product', productId],
        queryFn: () => fetch(productId!),
        enabled: !!productId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Product) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            return add(payload as Product);
        },
        onSuccess: () => {
            notifySuccess('Product created');
            queryClient.invalidateQueries({queryKey: ['products']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Product) => {
            if (!productId) throw new Error('productId is required parameter');
            return update(productId, values);
        },
        onSuccess: () => {
            notifySuccess('Product updated');
            queryClient.invalidateQueries({queryKey: ['products']});
            queryClient.invalidateQueries({queryKey: ['product', productId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!productId) throw new Error('productId is required parameter');
            return deleteRecord(productId);
        },
        onSuccess: () => {
            notifySuccess('Product deleted');
            queryClient.invalidateQueries({queryKey: ['products']});
        },
        onError: notifyError,
    });

    return {
        record: productQuery.data,
        loading: productQuery.isLoading,
        fetchRecord: productQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
